import styled from '@emotion/styled';

import MemoOffIcon from '@/assets/icons/icon_memo_off.svg';
import MemoOnIcon from '@/assets/icons/icon_memo_on.svg';
import CloseIcon from '@/assets/icons/icon_nav_open_black.svg';
import NextIcon from '@/assets/icons/icon_next_gray.svg';
import { mq } from '@/utility/mediaQuery';
import { defaultTheme } from '@/utility/theme';
import { disclosure, hoverOpacity } from '@/utility/theme/animation';

const BaseMemoIcon = styled.i(() =>
  mq({
    display: 'inline-block',
    marginLeft: defaultTheme.spacing.xs,
    marginBottom: '-1px',
    width: '12px',
    height: '14px',
    cursor: 'pointer',
    ...hoverOpacity,
  }),
);

const getIconCss = (
  url: string,
  width: string | string[],
  height: string | string[],
) => {
  return {
    display: 'inline-block',
    width,
    height,
    backgroundImage: `url(${url})`,
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
  };
};

export const Container = styled.div(() =>
  mq({
    width: '100%',
    maxWidth: '840px',
  }),
);

export const Headline = styled.h1(() =>
  mq({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    fontSize: defaultTheme.font.size.xl,
    fontWeight: 'bold',
    color: defaultTheme.text.default,
    padding: [
      `${defaultTheme.spacing.s} 0 ${defaultTheme.spacing.l}`,
      `${defaultTheme.spacing.s} 0 ${defaultTheme.spacing.l}`,
      `0 0 ${defaultTheme.spacing.xxl}`,
      `0 0 ${defaultTheme.spacing.xxl}`,
    ],
    margin: 0,
  }),
);

export const DetailWrapper = styled.div(() =>
  mq({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: defaultTheme.background.white,
    marginTop: defaultTheme.spacing.s,
  }),
);

export const DetailContainer = styled.div(() =>
  mq({
    width: '100%',
    padding: `${defaultTheme.spacing.s} ${defaultTheme.spacing.l}`,
  }),
);

export const AccordionWrapper = styled.div((props: { isOpen: boolean }) =>
  mq({
    ...disclosure(props.isOpen, '500px'),
  }),
);

export const SubTitle = styled.h1(() =>
  mq({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'left',
    alignItems: 'left',
    width: '100%',
    fontSize: defaultTheme.font.size.l,
    color: defaultTheme.text.default,
    padding: [
      `${defaultTheme.spacing.s} 0 ${defaultTheme.spacing.l}`,
      `${defaultTheme.spacing.s} 0 ${defaultTheme.spacing.l}`,
      `${defaultTheme.spacing.s} 0 ${defaultTheme.spacing.m}`,
      `${defaultTheme.spacing.s} 0 ${defaultTheme.spacing.m}`,
    ],
    margin: 0,
  }),
);

export const Table = styled.table(
  mq({
    width: '100%',
    border: `solid ${defaultTheme.border.color.black}`,
    borderWidth: '1px 0',
    marginBottom: defaultTheme.spacing.m,
  }),
);

export const TableRow = styled.tr(() =>
  mq({
    borderBottom: `1px solid ${defaultTheme.border.color.black}`,
    fontSize: [
      defaultTheme.font.size.s,
      defaultTheme.font.size.s,
      defaultTheme.font.size.m,
      defaultTheme.font.size.m,
    ],
  }),
);

export const BasicData = styled.td(() =>
  mq({
    // width: '80%',
    textAlign: 'left',
    verticalAlign: 'middle',
    borderBottom: `1px solid ${defaultTheme.border.color.black}`,
    padding: [
      defaultTheme.spacing.s,
      defaultTheme.spacing.s,
      defaultTheme.spacing.m,
      defaultTheme.spacing.m,
    ],
  }),
);

export const BasicHeader = styled.th(() =>
  mq({
    width: ['150px', '150px', '150px', '150px'],
    textAlign: 'left',
    verticalAlign: 'middle',
    fontWeight: 'inherit',
    backgroundColor: defaultTheme.background.mediumGray,
    borderBottom: `1px solid ${defaultTheme.border.color.black}`,
    padding: [
      defaultTheme.spacing.s,
      defaultTheme.spacing.s,
      defaultTheme.spacing.m,
      defaultTheme.spacing.m,
    ],
  }),
);

export const TableTitle = styled.h2(() =>
  mq({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'left',
    alignItems: 'left',
    width: '100%',
    fontSize: defaultTheme.font.size.m,
    color: defaultTheme.text.default,
    margin: 0,
    paddingBottom: defaultTheme.spacing.s,
  }),
);

export const TableData = styled.td(() =>
  mq({
    textAlign: 'center',
    verticalAlign: 'middle',
    borderBottom: `1px solid ${defaultTheme.border.color.black}`,
    borderRight: `1px solid ${defaultTheme.border.color.mediumLightGray}`,
    padding: [
      defaultTheme.spacing.s,
      defaultTheme.spacing.s,
      defaultTheme.spacing.m,
      defaultTheme.spacing.m,
    ],
  }),
);

export const TableHeader = styled.th(() =>
  mq({
    textAlign: 'center',
    verticalAlign: 'middle',
    fontWeight: 'inherit',
    fontSize: defaultTheme.font.size.s,
    backgroundColor: defaultTheme.background.mediumGray,
    borderBottom: `1px solid ${defaultTheme.border.color.black}`,
    borderRight: `1px solid ${defaultTheme.border.color.mediumLightGray}`,
    padding: [
      defaultTheme.spacing.s,
      defaultTheme.spacing.s,
      defaultTheme.spacing.m,
      defaultTheme.spacing.m,
    ],
  }),
);

export const SideButtonArea = styled.div(() =>
  mq({
    //width: '100%',
    textAlign: 'right',
    paddingTop: defaultTheme.spacing.s,
    paddingRight: defaultTheme.spacing.m,
    '& > a': {
      fontSize: defaultTheme.font.size.s,
      textDecorationLine: 'underline',
      ...hoverOpacity,
    },
    '& > button': {
      textDecorationLine: 'underline',
    },
  }),
);

export const LinkButtonArea = styled.div(() =>
  mq({
    width: '100%',
    textAlign: 'center',
    padding: `${defaultTheme.spacing.l} 20%`,
    '& > a': {
      fontSize: defaultTheme.font.size.s,
      textDecorationLine: 'underline',
      ...hoverOpacity,
    },
    '& > button': {
      textDecorationLine: 'underline',
    },
  }),
);

export const NotificationArea = styled.ul(() =>
  mq({
    borderTop: `1px solid ${defaultTheme.border.color.mediumLightGray}`,
  }),
);

export const NotificationItem = styled.li(() =>
  mq({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${defaultTheme.border.color.mediumLightGray}`,
    padding: `${defaultTheme.spacing.l} 0`,
  }),
);

export const RaceHistoryArea = styled.ul(() =>
  mq({
    borderTop: `1px solid ${defaultTheme.border.color.mediumLightGray}`,
  }),
);

export const RaceHistoryItem = styled.li(() =>
  mq({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${defaultTheme.border.color.mediumLightGray}`,
    padding: `${defaultTheme.spacing.l} 0`,
  }),
);

export const RaceHistoryLinkArea = styled.span(() =>
  mq({
    width: '80%',
    padding: `${defaultTheme.spacing.l} 0`,
  }),
);

export const RaceHistoryLinkAverageScore = styled.span(() =>
  mq({
    float: 'right',
  }),
);

export const StyledLink = styled.a(() =>
  mq({
    cursor: 'pointer',
    ...hoverOpacity,
  }),
);

export const ShortendRaceEventName = styled.span(() =>
  mq({
    fontSize: '10px',
    background: defaultTheme.background.darkGray,
    borderRadius: '2px',
    color: defaultTheme.color.white,
    marginRight: '2px',
    padding: '2px',
  }),
);

export const Rank = styled.span(() =>
  mq({
    fontSize: defaultTheme.font.size.s,
    marginRight: defaultTheme.spacing.xs,
    textDecorationLine: 'underline',
  }),
);

export const MemoOn = styled(BaseMemoIcon)(() =>
  mq({
    background: `url(${MemoOnIcon}) no-repeat center`,
  }),
);

export const MemoOff = styled(BaseMemoIcon)(() =>
  mq({
    background: `url(${MemoOffIcon}) no-repeat center`,
  }),
);

export const VelodromeName = styled.span(() =>
  mq({
    fontSize: defaultTheme.font.size.s,
    lineHeight: defaultTheme.font.height.base,
    marginRight: defaultTheme.spacing.s,
  }),
);

export const Icons = styled.span(() =>
  mq({
    marginRight: defaultTheme.spacing.m,
    alignItems: 'center',
    '& > img': {
      verticalAlign: 'text-bottom',
    },
    '& > i': {
      marginLeft: '0px',
      marginBottom: '-3px',
    },
  }),
);

export const ConditionList = styled.dl(() =>
  mq({
    borderTop: `1px solid ${defaultTheme.border.color.mediumLightGray}`,
    marginTop: defaultTheme.spacing.xxl,
  }),
);

export const ConditionItem = styled.div(() =>
  mq({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${defaultTheme.border.color.mediumLightGray}`,
    padding: `${defaultTheme.spacing.m} 0`,
    '& > dt': {
      color: defaultTheme.text.gray,
      fontSize: defaultTheme.font.size.s,
      fontWeight: 'normal',
    },
    '& > dd': {
      fontSize: [
        defaultTheme.font.size.s,
        defaultTheme.font.size.s,
        defaultTheme.font.size.m,
        defaultTheme.font.size.m,
      ],
    },
  }),
);

export const InputWrapper = styled.div(() =>
  mq({
    display: 'flex',
    alignItems: 'flex-end',
    '& > div': {
      width: ['210px', '210px', '320px', '320px'],
    },
  }),
);

export const ButtonWrapper = styled.div(() =>
  mq({
    display: 'grid',
    gridTemplateColumns: [
      '1fr',
      '1fr',
      'repeat(auto-fit, 320px)',
      'repeat(auto-fit, 320px)',
    ],
    gridTemplateRows: '56px',
    alignContent: 'center',
    justifyContent: 'center',
    gridGap: '16px',
    margin: `${defaultTheme.spacing.xxl} auto 0`,
    paddingBottom: defaultTheme.spacing.s,
    width: ['100%', '100%', '656px', '656px'],
    '& > button:last-of-type': {
      order: [-1, -1, 1],
    },
  }),
);

export const ButtonMessageWrapper = styled.div(() =>
  mq({
    display: 'grid',
    gridTemplateRows: '32px',
    alignContent: 'center',
    justifyContent: 'center',
    margin: `0 auto 0`,
    width: ['100%', '100%', '656px', '656px'],
  }),
);

export const AccordionButton = styled.button((props: { isOpen: boolean }) =>
  mq({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    width: '100%',
    height: props.isOpen ? '24px' : '48px',
    border: 'none',
    '& > span': {
      paddingRight: defaultTheme.spacing.l,
    },
  }),
);

export const AccordionCloseIcon = styled.i((props: { isOpen: boolean }) =>
  mq({
    ...getIconCss(CloseIcon, '18px', '10px'),
    transform: `rotate(${props.isOpen ? '0deg' : '180deg'})`,
    transition: '.2s all ease-in-out',
  }),
);

export const SelectArea = styled.div(() =>
  mq({
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: defaultTheme.background.white,
    margin: [
      `${defaultTheme.spacing.s} 0`,
      `${defaultTheme.spacing.s} 0`,
      `${defaultTheme.spacing.m} 0`,
      `${defaultTheme.spacing.m} 0`,
    ],
  }),
);

export const DetailSelectors = styled.div(() =>
  mq({
    display: 'flex',
    justifyContent: ['space-between', 'space-between', 'center', 'center'],
    marginTop: defaultTheme.spacing.m,
    fontSize: [
      defaultTheme.font.size.s,
      defaultTheme.font.size.s,
      defaultTheme.font.size.m,
      defaultTheme.font.size.m,
    ],
    '& > div': {
      marginLeft: [0, 0, defaultTheme.spacing.xxl, defaultTheme.spacing.xxl],
    },
    '& > span': {
      marginTop: defaultTheme.spacing.m,
      marginLeft: [0, 0, defaultTheme.spacing.xxl, defaultTheme.spacing.xxl],
    },
  }),
);

export const SortSelector = styled.div(() =>
  mq({
    display: 'flex',
    alignItems: 'center',
    '& > span': {
      marginRight: defaultTheme.spacing.s,
    },
  }),
);

export const LinkList = styled.ul(() =>
  mq({
    '& > a, & > li, & > span': {
      display: 'block',
      fontSize: [
        defaultTheme.font.size.s,
        defaultTheme.font.size.s,
        defaultTheme.font.size.m,
        defaultTheme.font.size.m,
      ],
      borderBottom: `1px solid ${defaultTheme.border.color.mediumLightGray}`,
      padding: `${defaultTheme.spacing.l} 0`,
      ...hoverOpacity,
    },
  }),
);

export const ListItem = styled.li(() =>
  mq({
    display: 'grid',
    gridTemplateColumns: '1fr 8.21px',
    alignItems: 'center',
    lineHeight: defaultTheme.font.height.l,
    '&:after': {
      content: '""',
      display: 'block',
      height: '14px',
      width: '8.21px',
      backgroundImage: `url(${NextIcon})`,
      backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
    },
  }),
);

export const EmptyMessage = styled.div(() =>
  mq({
    width: '100%',
    color: defaultTheme.text.gray,
    textAlign: 'center',
    backgroundColor: defaultTheme.background.white,
    padding: `40px 0`,
  }),
);

export const Note = styled.p(() =>
  mq({
    color: defaultTheme.text.gray,
    fontSize: defaultTheme.font.size.xs,
    lineHeight: defaultTheme.font.height.base,
    marginTop: defaultTheme.spacing.s,
    '&::before': {
      content: '"※"',
    },
  }),
);

export const ErrorNote = styled.span(() =>
  mq({
    fontSize: defaultTheme.font.size.xs,
    lineHeight: defaultTheme.font.height.base,
    marginTop: defaultTheme.spacing.s,
    color: defaultTheme.text.error,
    '&::before': {
      content: '"※"',
    },
  }),
);

export const LoadingWrapper = styled.div(() =>
  mq({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    maxWidth: '840px',
    backgroundColor: defaultTheme.background.white,
    padding: '88px 0',
    marginTop: defaultTheme.spacing.s,
  }),
);
